import { CalendarSettings, CalendarCallback } from '../features/calendar/CalendarSettings.js';
import { AdminSessionBoundary, useAdminSessionActions } from './AdminSessionBoundary.js';
import { StartupScreen } from '../features/session/StartupScreen.js';
import { CollectionNavigationProvider } from '../ui/collection-navigation.js';
import { announceAvatarChange } from '../features/crm/ClientAvatar.js';
import { useEditingPayment } from '../features/crm/use-editing-payment.js';
import {
  AdminAssetResponse,
  AssetUsageLocationsResponse,
  AdminUserResponse,
  AdminUsersResponse,
  ClientResponse,
  ClientsResponse,
  JournalPostResponse,
  JournalPostsResponse,
  PageResponse,
  PagesResponse,
  PendingChangesResponse,
  ProductionReleaseResponse,
  ProductionReleaseSummary,
  PricingResponse,
  ReleaseBuildAttemptResponse,
  ReleaseBuildAttemptsResponse,
  SettingsResponse,
  SessionResponse,
  SessionStatusHistoryResponse,
  SessionsResponse,
  ShootResponse,
  ShootsResponse,
  DeleteShootResponse,
  CrmShootDeletionPreview,
  DeleteCrmShootResponse,
  DeleteClientResponse,
  type CreateJournalPostRequest,
  type CreateClientRequest,
  type PendingChange,
  type PageSummary,
  type SiteAccess,
  type AdminRole,
  type UpdateClientRequest,
  type UpdateSessionRequest,
} from '@diana/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useOutletContext,
} from 'react-router-dom';
import { z } from 'zod';

import { plainToRichText } from '../components/RichTextField.js';
import { ClientEditor, NewClientEditor } from '../features/crm/ClientEditor.js';
import { ClientsList } from '../features/crm/ClientsList.js';
import { packageDurationMinutes } from '../features/crm/package-duration.js';
import { CrmShootEditor } from '../features/crm/CrmShootEditor.js';
import { NewShootEditor } from '../features/crm/NewShootEditor.js';
import { moveShoot } from '../features/crm/move-shoot.js';
import { CrmDashboardRoute } from '../features/dashboard/CrmDashboard.js';
import { CrmShootsList } from '../features/crm/CrmShootsList.js';
import { useRequestNotes } from '../features/crm/use-request-notes.js';
import { JournalEditor } from '../features/journal/JournalEditor.js';
import { JournalList } from '../features/journal/JournalList.js';
import { MediaHeader, MediaLibrary } from '../features/media/MediaLibrary.js';
import { MediaItem } from '../features/media/MediaItem.js';
import { loadAllAdminAssets } from '../features/media/load-assets.js';
import { PageEditor } from '../features/pages/PageEditor.js';
import { PagesList } from '../features/pages/PagesList.js';
import { usePageCovers } from '../features/pages/use-page-covers.js';
import { adminPath, PublishDrawer } from '../features/preview/PublishDrawer.js';
import { PricingEditor } from '../features/pricing/PricingEditor.js';
import { SettingsEditor } from '../features/settings/SettingsEditor.js';
import type { SettingsOutletContext } from '../features/settings/settings-sections.js';
import { SettingsWorkspace } from '../features/settings/SettingsWorkspace.js';
import { ShootEditor } from '../features/shoots/ShootEditor.js';
import { ShootsList } from '../features/shoots/ShootsList.js';
import { UsersEditor } from '../features/users/UsersEditor.js';
import { useAdminApi } from './AdminApiContext.js';
import { AdminApiError } from '../lib/api-client.js';
import { EditorCommandProvider } from './EditorCommandContext.js';
import { Shell } from './Shell.js';
import { SiteHub } from './SiteHub.js';
import { ShellPublishingProvider } from './ShellPublishingContext.js';
import { DianaUIProvider } from '../ui/index.js';

const UIKitPage = lazy(() => import('../features/ui-kit/UIKitPage.js'));
const AdminDesignLab = lazy(() => import('../features/design-lab/AdminDesignLab.js'));
const ProductionReleaseStatusResponse = z
  .object({ release: ProductionReleaseSummary.passthrough() })
  .strict();

function preserveDraftOnRefetch(error: unknown) {
  return (
    error instanceof AdminApiError &&
    (error.kind === 'unauthorized' ||
      error.kind === 'unavailable' ||
      error.code === 'SESSION_CHANGED')
  );
}

export function AdminRouter() {
  const location = useLocation();
  const designLabOpen = import.meta.env.DEV && location.pathname === '/design-lab';

  if (designLabOpen) {
    return (
      <Routes>
        <Route
          path="/design-lab"
          element={
            <Suspense fallback={<SessionState title="Opening design lab" />}>
              <AdminDesignLab />
            </Suspense>
          }
        />
      </Routes>
    );
  }

  return (
    <AdminSessionBoundary>{(me) => <AuthenticatedAdminRoutes session={me} />}</AdminSessionBoundary>
  );
}

function AuthenticatedAdminRoutes({
  session,
}: {
  session: Awaited<ReturnType<ReturnType<typeof useAdminApi>['getMe']>>;
}) {
  return (
    <Routes>
      <Route
        path="/ui-kit"
        element={
          <Suspense fallback={<SessionState title="Opening UI kit" />}>
            <UIKitPage />
          </Suspense>
        }
      />
      <Route
        element={
          <EditorCommandProvider>
            <ConnectedShell user={session.user} />
          </EditorCommandProvider>
        }
      >
        <Route index element={<Navigate to="/pages" replace />} />
        <Route path="/site" element={<SiteHub />} />
        <Route path="/pages" element={<PagesRoute />} />
        <Route path="/pages/:id" element={<PageRoute />} />
        <Route path="/packages" element={<PricingRoute />} />
        <Route path="/packages/:planId" element={<PricingRoute />} />
        <Route path="/pricing" element={<Navigate to="/packages" replace />} />
        <Route path="/pricing/:planId" element={<LegacyPricingRedirect />} />
        <Route path="/settings" element={<SettingsWorkspace role={session.user.role} />}>
          <Route index element={<SettingsRoute />} />
          <Route
            path="calendar"
            element={
              session.user.role === 'Owner' ? (
                <CalendarSettings />
              ) : (
                <Navigate to="/settings" replace />
              )
            }
          />
          <Route
            path="calendar/callback"
            element={
              session.user.role === 'Owner' ? (
                <CalendarCallback />
              ) : (
                <Navigate to="/settings" replace />
              )
            }
          />
          <Route
            path="users"
            element={
              session.user.role === 'Owner' ? <UsersRoute /> : <Navigate to="/settings" replace />
            }
          />
        </Route>
        <Route path="/settings/media" element={<MediaRoute />} />
        <Route path="/settings/media/:id" element={<MediaRoute />} />
        <Route path="/crm/dashboard" element={<CrmDashboardRoute />} />
        <Route path="/crm" element={<Navigate to="/crm/dashboard" replace />} />
        <Route path="/crm/shoots" element={<CrmShootsRoute />} />
        <Route path="/crm/shoots/new" element={<NewShootRoute />} />
        <Route path="/crm/shoots/:id" element={<CrmShootRoute />} />
        <Route path="/crm/clients" element={<ClientsRoute />} />
        <Route path="/crm/clients/new" element={<NewClientRoute />} />
        <Route path="/crm/clients/:id" element={<ClientRoute />} />
        <Route path="/requests" element={<LegacyRequestsRedirect />} />
        <Route
          path="/users"
          element={
            session.user.role === 'Owner' ? (
              <Navigate to="/settings/users" replace />
            ) : (
              <Navigate to="/settings" replace />
            )
          }
        />
        <Route path="/shoots" element={<ShootsRoute />} />
        <Route path="/shoots/:id" element={<ShootRoute />} />
        <Route path="/journal" element={<JournalRoute />} />
        <Route path="/journal/:id" element={<JournalPostRoute />} />
        <Route path="/media" element={<LegacyMediaRedirect />} />
        <Route path="/publish" element={<Navigate to="/pages?publish=1" replace />} />
        <Route path="/preview" element={<Navigate to="/pages?publish=1" replace />} />
        <Route path="*" element={<Navigate to="/pages" replace />} />
      </Route>
    </Routes>
  );
}

function ConnectedShell({ user }: { user: Parameters<typeof Shell>[0]['user'] }) {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const sessionActions = useAdminSessionActions();
  const logout = { mutate: sessionActions.logout };
  const pending = useQuery({
    queryKey: ['pending-changes'],
    queryFn: () => api.get('/api/v1/admin/releases/pending', PendingChangesResponse),
    refetchOnWindowFocus: true,
  });
  const requests = useQuery({
    queryKey: ['request-count'],
    queryFn: () => api.get('/api/v1/admin/sessions?status=NewRequest', SessionsResponse),
    staleTime: 60_000,
  });
  useEffect(
    () =>
      api.subscribeToMutations(() => {
        void queryClient.invalidateQueries({ queryKey: ['pending-changes'] });
      }),
    [api, queryClient],
  );
  useEffect(() => {
    if (new URLSearchParams(location.search).get('publish') === '1') setPublishOpen(true);
  }, [location.search]);

  const closePublish = () => {
    setPublishOpen(false);
    if (new URLSearchParams(location.search).has('publish')) {
      navigate(location.pathname, { replace: true });
    }
  };

  const navigateToChange = (change: PendingChange) => {
    const path = adminPath(change);
    if (!path) return;
    setPublishOpen(false);
    navigate(path);
  };
  const openPublish = () => {
    void pending.refetch();
    setPublishOpen(true);
  };
  return (
    <CollectionNavigationProvider>
      <DianaUIProvider>
        <ShellPublishingProvider
          value={{
            user,
            pendingCount: pending.data?.count ?? 0,
            pendingLoading: pending.isPending,
            publishing,
            openPublish,
            logout: () => logout.mutate(),
          }}
        >
          <Shell
            user={user}
            newRequestCount={requests.data?.sessions.length ?? 0}
            pendingCount={pending.data?.count ?? 0}
            pendingLoading={pending.isPending}
            publishing={publishing}
            onPendingChanges={openPublish}
            onLogout={() => logout.mutate()}
            publishPanel={
              pending.data ? (
                <PublishDrawer
                  open={publishOpen}
                  overview={pending.data}
                  close={closePublish}
                  navigateTo={navigateToChange}
                  publish={(sourceRevisions) =>
                    api.request('/api/v1/admin/releases/production', {
                      method: 'POST',
                      body: { sourceRevisions },
                      schema: ProductionReleaseResponse,
                    })
                  }
                  load={async (releaseId) => {
                    const response = await api.get(
                      `/api/v1/admin/releases/${releaseId}`,
                      ProductionReleaseStatusResponse,
                    );
                    return response.release;
                  }}
                  loadAttempts={async (releaseId) => {
                    const response = await api.get(
                      `/api/v1/admin/releases/${releaseId}/attempts`,
                      ReleaseBuildAttemptsResponse,
                    );
                    return response.attempts;
                  }}
                  retry={(releaseId) =>
                    api.request(`/api/v1/admin/releases/${releaseId}/retry`, {
                      method: 'POST',
                      schema: ReleaseBuildAttemptResponse,
                    })
                  }
                  onPublished={() => {
                    void queryClient.invalidateQueries({ queryKey: ['pending-changes'] });
                  }}
                  onPublishingChange={setPublishing}
                />
              ) : null
            }
          />
        </ShellPublishingProvider>
      </DianaUIProvider>
    </CollectionNavigationProvider>
  );
}

function CrmShootsRoute() {
  const api = useAdminApi();
  const editingPayment = useEditingPayment();
  const queryClient = useQueryClient();
  const shoots = useQuery({
    queryKey: ['crm-shoots'],
    queryFn: () => api.get('/api/v1/admin/sessions', SessionsResponse),
  });
  const requestNotes = useRequestNotes(shoots.data?.sessions ?? []);
  if (shoots.isPending) return <SessionState title="Opening shoots" />;
  if (shoots.isError) return <EditorError onRetry={() => void shoots.refetch()} />;
  return (
    <>
      {editingPayment.dialog}
      <CrmShootsList
        shoots={shoots.data.sessions}
        requestNotes={requestNotes}
        deletionPreview={(shoot) =>
          api.get(`/api/v1/admin/sessions/${shoot.id}/deletion-preview`, CrmShootDeletionPreview)
        }
        remove={async (shoot, body) => {
          await api.request(`/api/v1/admin/sessions/${shoot.id}`, {
            method: 'DELETE',
            body,
            schema: DeleteCrmShootResponse,
          });
          queryClient.removeQueries({ queryKey: ['crm-shoot', shoot.id] });
          await Promise.all(
            [['crm-shoots'], ['crm-clients'], ['request-count']].map((queryKey) =>
              queryClient.invalidateQueries({ queryKey }),
            ),
          );
        }}
        onMove={async (shoot, status) => {
          try {
            const response = await moveShoot(api, shoot, status, editingPayment.request);
            if ('canceled' in response && response.canceled) return false;
            queryClient.setQueryData(['crm-shoot', shoot.id], response);
            queryClient.setQueryData<SessionsResponse>(['crm-shoots'], (current) =>
              current
                ? {
                    ...current,
                    sessions: current.sessions.map((item) =>
                      item.id === shoot.id
                        ? {
                            ...item,
                            status: response.session.status,
                            revision: response.session.revision,
                            updatedAt: response.session.updatedAt,
                          }
                        : item,
                    ),
                  }
                : current,
            );
          } catch (error) {
            // A later detail editor must not mount an obsolete cached aggregate.
            queryClient.removeQueries({ queryKey: ['crm-shoot', shoot.id], exact: true });
            throw error;
          } finally {
            await Promise.all(
              [['crm-shoots'], ['crm-clients'], ['request-count']].map((queryKey) =>
                queryClient.invalidateQueries({ queryKey }).catch(() => undefined),
              ),
            );
          }
        }}
      />
    </>
  );
}

function NewShootRoute() {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pricing = useQuery({
    queryKey: ['pricing'],
    queryFn: () => api.get('/api/v1/admin/pricing', PricingResponse),
  });
  const clients = useQuery({
    queryKey: ['crm-clients', 'create-request'],
    queryFn: () => api.get('/api/v1/admin/clients?archived=active', ClientsResponse),
    retry: false,
  });
  return (
    <NewShootEditor
      packages={pricing.data?.pricing.plans ?? []}
      packagesError={pricing.isError}
      clients={clients.data?.clients ?? []}
      clientsLoading={clients.isPending}
      clientsError={clients.isError}
      retryClients={() => {
        void clients.refetch();
      }}
      createClient={async (body) => {
        const response = await api.request('/api/v1/admin/clients', {
          method: 'POST',
          body,
          schema: ClientResponse,
        });
        queryClient.setQueryData(['crm-client', response.client.id], response);
        // The client is already saved even if the next request fails.
        void queryClient.invalidateQueries({ queryKey: ['crm-clients'] }).catch(() => undefined);
        return response.client;
      }}
      createSession={async (body) =>
        (
          await api.request('/api/v1/admin/sessions', {
            method: 'POST',
            body,
            schema: SessionResponse,
          })
        ).session
      }
      onCreated={(session) => {
        queryClient.setQueryData(['crm-shoot', session.id], { session });
        // Creation has succeeded. Refresh errors must never offer another create.
        for (const queryKey of [['crm-shoots'], ['crm-clients'], ['request-count']]) {
          void queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
        }
        navigate(`/crm/shoots/${session.id}`, { replace: true });
      }}
      onCancel={() => navigate('/crm/shoots')}
    />
  );
}

function CrmShootRoute() {
  const navigate = useNavigate();
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const { id = '' } = useParams();
  const shoot = useQuery({
    queryKey: ['crm-shoot', id],
    queryFn: () => api.get(`/api/v1/admin/sessions/${id}`, SessionResponse),
    enabled: Boolean(id),
  });
  const pricing = useQuery({
    queryKey: ['pricing'],
    queryFn: () => api.get('/api/v1/admin/pricing', PricingResponse),
  });
  const history = useQuery({
    queryKey: ['crm-shoot-history', id, shoot.data?.session.revision],
    queryFn: () =>
      api.get(`/api/v1/admin/sessions/${id}/status-history`, SessionStatusHistoryResponse),
    enabled: Boolean(shoot.data),
  });
  if (shoot.isPending || pricing.isPending) return <SessionState title="Opening shoot" />;
  if (shoot.isError && (!shoot.data || !preserveDraftOnRefetch(shoot.error)))
    return <EditorError onRetry={() => void shoot.refetch()} />;
  return (
    <CrmShootEditor
      shoot={shoot.data.session}
      contactsChanged={(session) => {
        queryClient.setQueryData(['crm-shoot', session.id], { session });
        for (const queryKey of [['crm-shoots'], ['crm-clients']])
          void queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
      }}
      packages={pricing.data?.pricing.plans ?? []}
      history={{
        entries: history.data?.entries ?? [],
        pending: history.isPending,
        error: history.isError,
        retry: () => void history.refetch(),
      }}
      defaultDurationMinutes={packageDurationMinutes(
        pricing.data?.pricing.plans.find(
          (plan) => plan.key === shoot.data.session.requestedPackageKey,
        )?.payload,
      )}
      deletionPreview={() =>
        api.get(`/api/v1/admin/sessions/${id}/deletion-preview`, CrmShootDeletionPreview)
      }
      remove={async (body) => {
        await api.request(`/api/v1/admin/sessions/${id}`, {
          method: 'DELETE',
          body,
          schema: DeleteCrmShootResponse,
        });
        navigate('/crm/shoots', { replace: true });
        queryClient.removeQueries({ queryKey: ['crm-shoot', id] });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['crm-shoots'] }),
          queryClient.invalidateQueries({ queryKey: ['crm-clients'] }),
          queryClient.invalidateQueries({ queryKey: ['request-count'] }),
        ]);
      }}
      save={async (body: UpdateSessionRequest) => {
        const response = await api.request(`/api/v1/admin/sessions/${id}`, {
          method: 'PUT',
          body,
          schema: SessionResponse,
        });
        queryClient.setQueryData(['crm-shoot', id], response);
        await queryClient.invalidateQueries({ queryKey: ['crm-shoots'] });
        await queryClient.invalidateQueries({ queryKey: ['request-count'] });
        return response.session;
      }}
    />
  );
}

function ClientsRoute() {
  const api = useAdminApi();
  const clients = useQuery({
    queryKey: ['crm-clients'],
    queryFn: () => api.get('/api/v1/admin/clients?archived=all', ClientsResponse),
  });
  const shoots = useQuery({
    queryKey: ['crm-shoots'],
    queryFn: () => api.get('/api/v1/admin/sessions', SessionsResponse),
  });
  if (clients.isPending || shoots.isPending) return <SessionState title="Opening clients" />;
  if (clients.isError || shoots.isError) {
    return (
      <EditorError
        onRetry={() => {
          void clients.refetch();
          void shoots.refetch();
        }}
      />
    );
  }
  return <ClientsList clients={clients.data.clients} shoots={shoots.data.sessions} />;
}

function ClientRoute() {
  const navigate = useNavigate();
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const { id = '' } = useParams();
  const client = useQuery({
    queryKey: ['crm-client', id],
    queryFn: () => api.get(`/api/v1/admin/clients/${id}`, ClientResponse),
    enabled: Boolean(id),
  });
  const shoots = useQuery({
    queryKey: ['crm-shoots'],
    queryFn: () => api.get('/api/v1/admin/sessions', SessionsResponse),
  });
  if (client.isPending || shoots.isPending) return <SessionState title="Opening client" />;
  if (
    (client.isError && (!client.data || !preserveDraftOnRefetch(client.error))) ||
    (shoots.isError && (!shoots.data || !preserveDraftOnRefetch(shoots.error)))
  ) {
    return (
      <EditorError
        onRetry={() => {
          void client.refetch();
          void shoots.refetch();
        }}
      />
    );
  }
  return (
    <ClientEditor
      avatarApi={api}
      client={client.data.client}
      shoots={shoots.data.sessions}
      remove={async (body) => {
        await api.request(`/api/v1/admin/clients/${id}`, {
          method: 'DELETE',
          body,
          schema: DeleteClientResponse,
        });
        navigate('/crm/clients', { replace: true });
        queryClient.removeQueries({ queryKey: ['crm-client', id], exact: true });
        await queryClient.invalidateQueries({ queryKey: ['crm-clients'] });
      }}
      save={async (body: UpdateClientRequest) => {
        const response = await api.request(`/api/v1/admin/clients/${id}`, {
          method: 'PUT',
          body,
          schema: ClientResponse,
        });
        queryClient.setQueryData(['crm-client', id], response);
        announceAvatarChange(id);
        await queryClient.invalidateQueries({ queryKey: ['crm-clients'] });
        await queryClient.invalidateQueries({ queryKey: ['crm-shoots'] });
        return response.client;
      }}
    />
  );
}

function NewClientRoute() {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return (
    <NewClientEditor
      create={async (body: CreateClientRequest) => {
        const response = await api.request('/api/v1/admin/clients', {
          method: 'POST',
          body,
          schema: ClientResponse,
        });
        await queryClient.invalidateQueries({ queryKey: ['crm-clients'] });
        navigate(`/crm/clients/${response.client.id}`, { replace: true });
        return response.client;
      }}
    />
  );
}

function LegacyRequestsRedirect() {
  const location = useLocation();
  const requestId = new URLSearchParams(location.search).get('request');
  return <Navigate to={requestId ? `/crm/shoots/${requestId}` : '/crm/shoots'} replace />;
}

function PagesRoute() {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['pages'],
    queryFn: () => api.get('/api/v1/admin/pages', PagesResponse),
  });
  const covers = usePageCovers(query.data?.pages ?? []);
  const access = useMutation({
    mutationFn: async ({ page, siteAccess }: { page: PageSummary; siteAccess: SiteAccess }) => {
      const current = await api.get(`/api/v1/admin/pages/${page.id}`, PageResponse);
      const response = await api.request(`/api/v1/admin/pages/${page.id}/blocks`, {
        method: 'PUT',
        body: {
          expectedRevision: current.page.revision,
          blocks: current.page.blocks,
          payload: current.page.payload,
          siteAccess,
        },
        schema: PageResponse,
      });
      return response.page;
    },
    onSuccess: (page) => {
      queryClient.setQueryData(['pages'], (current: unknown) => {
        const parsed = PagesResponse.safeParse(current);
        if (!parsed.success) return current;
        const summary: PageSummary = {
          id: page.id,
          route: page.route,
          slug: page.slug,
          kind: page.kind,
          siteAccess: page.siteAccess,
          revision: page.revision,
          payload: page.payload,
        };
        return {
          pages: parsed.data.pages.map((entry) => (entry.id === page.id ? summary : entry)),
        };
      });
      queryClient.setQueryData(['page', page.id], { page });
    },
  });
  return query.isPending ? (
    <EditorLoading />
  ) : query.isError ? (
    <EditorError onRetry={() => void query.refetch()} />
  ) : (
    <PagesList
      pages={query.data.pages}
      covers={covers}
      changingId={access.isPending ? access.variables?.page.id : undefined}
      error={access.isError ? 'Could not update page access. Please try again.' : undefined}
      changeSiteAccess={async (page, siteAccess) => {
        await access.mutateAsync({ page, siteAccess });
      }}
    />
  );
}

function PageRoute() {
  const api = useAdminApi();
  const params = useParams();
  const id = params.id ?? '';
  const query = useQuery({
    queryKey: ['page', id],
    queryFn: () => api.get(`/api/v1/admin/pages/${id}`, PageResponse),
    enabled: id.length > 0,
  });
  const assets = useAssets();
  const isHome = query.data?.page.kind === 'Home';
  const isStructuredPage = Boolean(query.data) && !isHome;
  const shoots = useQuery({
    queryKey: ['shoots'],
    queryFn: () => api.get('/api/v1/admin/shoots', ShootsResponse),
    enabled: Boolean(query.data),
  });
  const journal = useQuery({
    queryKey: ['journal'],
    queryFn: () => api.get('/api/v1/admin/journal', JournalPostsResponse),
    enabled: isStructuredPage,
  });
  const pricing = useQuery({
    queryKey: ['pricing'],
    queryFn: () => api.get('/api/v1/admin/pricing', PricingResponse),
    enabled: isStructuredPage,
  });
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/api/v1/admin/settings', SettingsResponse),
    enabled: isHome,
  });
  const relatedPending =
    Boolean(query.data) &&
    (shoots.isPending || (isHome ? settings.isPending : journal.isPending || pricing.isPending));
  const relatedError =
    Boolean(query.data) &&
    ((shoots.isError && (!shoots.data || !preserveDraftOnRefetch(shoots.error))) ||
      (isHome
        ? settings.isError && (!settings.data || !preserveDraftOnRefetch(settings.error))
        : (journal.isError && (!journal.data || !preserveDraftOnRefetch(journal.error))) ||
          (pricing.isError && (!pricing.data || !preserveDraftOnRefetch(pricing.error)))));
  return query.isPending || assets.isPending || relatedPending ? (
    <EditorLoading />
  ) : (query.isError && (!query.data || !preserveDraftOnRefetch(query.error))) ||
    (assets.isError && (!assets.data || !preserveDraftOnRefetch(assets.error))) ||
    relatedError ? (
    <EditorError
      onRetry={() => {
        void query.refetch();
        void assets.refetch();
        void shoots.refetch();
        if (isHome) {
          void settings.refetch();
        } else {
          void journal.refetch();
          void pricing.refetch();
        }
      }}
    />
  ) : (
    <PageEditor
      page={query.data.page}
      assets={assets.data.assets}
      shoots={shoots.data?.shoots.filter((shoot) => shoot.siteAccess === 'Public') ?? []}
      journal={journal.data?.journal ?? []}
      pricing={pricing.data?.pricing}
      socialLinks={settings.data?.settings.payload.socialLinks ?? []}
      save={async (body) => {
        const response = await api.request(`/api/v1/admin/pages/${id}/blocks`, {
          method: 'PUT',
          body,
          schema: PageResponse,
        });
        return response.page;
      }}
    />
  );
}

function SettingsRoute() {
  const { section } = useOutletContext<SettingsOutletContext>();
  const api = useAdminApi();
  const query = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/api/v1/admin/settings', SettingsResponse),
  });
  const assets = useAssets();
  return query.isPending || assets.isPending ? (
    <EditorLoading />
  ) : (query.isError && (!query.data || !preserveDraftOnRefetch(query.error))) ||
    (assets.isError && (!assets.data || !preserveDraftOnRefetch(assets.error))) ? (
    <EditorError
      onRetry={() => {
        void query.refetch();
        void assets.refetch();
      }}
    />
  ) : (
    <SettingsEditor
      section={section}
      embedded
      settings={query.data.settings}
      assets={assets.data.assets}
      save={async (body) => {
        const response = await api.request('/api/v1/admin/settings', {
          method: 'PATCH',
          body,
          schema: SettingsResponse,
        });
        return response.settings;
      }}
    />
  );
}

function PricingRoute() {
  const api = useAdminApi();
  const { planId } = useParams();
  const query = useQuery({
    queryKey: ['pricing'],
    queryFn: () => api.get('/api/v1/admin/pricing', PricingResponse),
  });
  // Extras belong to PricingPage. This lookup must never gate package editing.
  const pages = useQuery({
    queryKey: ['pages'],
    queryFn: () => api.get('/api/v1/admin/pages', PagesResponse),
  });
  const pricingPage = pages.data?.pages.find((page) => page.kind === 'PricingPage');
  const assets = useAssets();
  return query.isPending || assets.isPending ? (
    <EditorLoading />
  ) : (query.isError && (!query.data || !preserveDraftOnRefetch(query.error))) ||
    (assets.isError && (!assets.data || !preserveDraftOnRefetch(assets.error))) ? (
    <EditorError
      onRetry={() => {
        void query.refetch();
        void assets.refetch();
      }}
    />
  ) : (
    <PricingEditor
      pricing={query.data.pricing}
      assets={assets.data.assets}
      planId={planId}
      extrasHref={pricingPage ? `/pages/${pricingPage.id}` : '/pages'}
      saveCollection={async (collection, expectedRevision, items) => {
        const response = await api.request(`/api/v1/admin/pricing/${collection}`, {
          method: 'PUT',
          body: { expectedRevision, items },
          schema: PricingResponse,
        });
        return response.pricing;
      }}
    />
  );
}

function LegacyPricingRedirect() {
  const { planId } = useParams();
  return <Navigate to={planId ? `/packages/${planId}` : '/packages'} replace />;
}

function UsersRoute() {
  const api = useAdminApi();
  const query = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/v1/admin/users', AdminUsersResponse),
  });
  if (query.isPending) return <EditorLoading />;
  if (query.isError && (!query.data || !preserveDraftOnRefetch(query.error)))
    return <EditorError onRetry={() => void query.refetch()} />;
  return (
    <UsersEditor
      embedded
      users={query.data.users}
      invite={async (body) => {
        const response = await api.request('/api/v1/admin/users/invite', {
          method: 'POST',
          body,
          schema: AdminUserResponse,
        });
        return response.user;
      }}
      changeRole={async (id, role: AdminRole) => {
        const response = await api.request(`/api/v1/admin/users/${id}/role`, {
          method: 'PATCH',
          body: { role },
          schema: AdminUserResponse,
        });
        return response.user;
      }}
      changeActive={async (id) => {
        const response = await api.request(`/api/v1/admin/users/${id}/active`, {
          method: 'PATCH',
          body: { active: false },
          schema: AdminUserResponse,
        });
        return response.user;
      }}
    />
  );
}

function LegacyMediaRedirect() {
  const location = useLocation();
  return <Navigate to={`/settings/media${location.search}`} replace />;
}

function MediaRoute() {
  const { id } = useParams();
  const location = useLocation();
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['assets', 'library'],
    queryFn: () => loadAllAdminAssets(api),
  });
  if (query.isPending) return <EditorLoading />;
  if (query.isError && (!query.data || !preserveDraftOnRefetch(query.error)))
    return <EditorError onRetry={() => void query.refetch()} />;
  const actions = {
    loadUsages: async (id) => {
      const response = await api.get(
        `/api/v1/admin/assets/${id}/usages`,
        AssetUsageLocationsResponse,
      );
      return response.usages;
    },
    updateAsset: async (id, body) => {
      const response = await api.request(`/api/v1/admin/assets/${id}`, {
        method: 'PATCH',
        body,
        schema: AdminAssetResponse,
      });
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
      return response.asset;
    },
    archiveAsset: async (id, expectedRevision) => {
      const response = await api.request(`/api/v1/admin/assets/${id}/archive`, {
        method: 'POST',
        body: { expectedRevision },
        schema: AdminAssetResponse,
      });
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
      return response.asset;
    },
  } satisfies import('../features/media/MediaLibrary.js').MediaActions;
  if (!id) return <MediaLibrary assets={query.data} {...actions} />;
  const asset = query.data.find((entry) => entry.id === id);
  if (!asset)
    return (
      <article className="editor media-item">
        <MediaHeader
          title="Media not found"
          backTo={`/settings/media${location.search}`}
          backLabel="Back to media"
        />
        <p className="media-library__note">This asset is not in the Media library.</p>
      </article>
    );
  return <MediaItem key={id} asset={asset} {...actions} />;
}

function ShootsRoute() {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const shoots = useQuery({
    queryKey: ['shoots'],
    queryFn: () => api.get('/api/v1/admin/shoots', ShootsResponse),
  });
  if (shoots.isPending) return <EditorLoading />;
  if (shoots.isError) {
    return (
      <EditorError
        onRetry={() => {
          void shoots.refetch();
        }}
      />
    );
  }
  return (
    <ShootsList
      shoots={shoots.data.shoots}
      onSiteAccessChange={async (shoot, siteAccess: SiteAccess) => {
        await api.request(`/api/v1/admin/shoots/${shoot.id}/site-access`, {
          method: 'PATCH',
          body: { siteAccess, expectedRevision: shoot.revision },
          schema: ShootResponse,
        });
        await queryClient.invalidateQueries({ queryKey: ['shoots'] });
      }}
      onOrderChange={async (nextShoots) => {
        await api.request('/api/v1/admin/shoots/order', {
          method: 'PATCH',
          body: { shootIds: nextShoots.map((shoot) => shoot.id) },
          schema: ShootsResponse,
        });
        await queryClient.invalidateQueries({ queryKey: ['shoots'] });
      }}
    />
  );
}

function ShootRoute() {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id ?? '';
  const shoot = useQuery({
    queryKey: ['shoot', id],
    queryFn: () => api.get(`/api/v1/admin/shoots/${id}`, ShootResponse),
    enabled: Boolean(id),
  });
  const assets = useAssets();
  if (shoot.isPending || assets.isPending) return <EditorLoading />;
  if (
    (shoot.isError && (!shoot.data || !preserveDraftOnRefetch(shoot.error))) ||
    (assets.isError && (!assets.data || !preserveDraftOnRefetch(assets.error)))
  ) {
    return (
      <EditorError
        onRetry={() => {
          void shoot.refetch();
          void assets.refetch();
        }}
      />
    );
  }
  return (
    <ShootEditor
      shoot={shoot.data.shoot}
      assets={assets.data.assets}
      save={async (body) => {
        const response = await api.request(`/api/v1/admin/shoots/${id}`, {
          method: 'PUT',
          body,
          schema: ShootResponse,
        });
        return response.shoot;
      }}
      remove={async (expectedRevision) => {
        await api.request(`/api/v1/admin/shoots/${id}`, {
          method: 'DELETE',
          body: { expectedRevision },
          schema: DeleteShootResponse,
        });
        await queryClient.invalidateQueries({ queryKey: ['shoots'] });
        await queryClient.invalidateQueries({ queryKey: ['assets'] });
        navigate('/shoots');
      }}
    />
  );
}

function JournalRoute() {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const journal = useQuery({
    queryKey: ['journal'],
    queryFn: () => api.get('/api/v1/admin/journal', JournalPostsResponse),
  });
  const assets = useAssets();
  if (journal.isPending || assets.isPending) return <EditorLoading />;
  if (
    (journal.isError && (!journal.data || !preserveDraftOnRefetch(journal.error))) ||
    (assets.isError && (!assets.data || !preserveDraftOnRefetch(assets.error)))
  ) {
    return (
      <EditorError
        onRetry={() => {
          void journal.refetch();
          void assets.refetch();
        }}
      />
    );
  }
  const active = assets.data.assets.filter((asset) => asset.archivedAt === null);
  return (
    <JournalList
      posts={journal.data.journal}
      assets={assets.data.assets}
      canCreate={active.length > 0}
      onCreate={async () => {
        if (!active[0]) return;
        const response = await api.request('/api/v1/admin/journal', {
          method: 'POST',
          body: newJournalPost(active[0].id),
          schema: JournalPostResponse,
        });
        await queryClient.invalidateQueries({ queryKey: ['journal'] });
        navigate(`/journal/${response.journal.id}`);
      }}
    />
  );
}

function JournalPostRoute() {
  const api = useAdminApi();
  const params = useParams();
  const id = params.id ?? '';
  const post = useQuery({
    queryKey: ['journal', id],
    queryFn: () => api.get(`/api/v1/admin/journal/${id}`, JournalPostResponse),
    enabled: Boolean(id),
  });
  const assets = useAssets();
  if (post.isPending || assets.isPending) return <EditorLoading />;
  if (
    (post.isError && (!post.data || !preserveDraftOnRefetch(post.error))) ||
    (assets.isError && (!assets.data || !preserveDraftOnRefetch(assets.error)))
  ) {
    return (
      <EditorError
        onRetry={() => {
          void post.refetch();
          void assets.refetch();
        }}
      />
    );
  }
  return (
    <JournalEditor
      post={post.data.journal}
      assets={assets.data.assets}
      save={async (body) => {
        const response = await api.request(`/api/v1/admin/journal/${id}`, {
          method: 'PUT',
          body,
          schema: JournalPostResponse,
        });
        return response.journal;
      }}
    />
  );
}

function useAssets() {
  const api = useAdminApi();
  return useQuery({
    queryKey: ['assets', 'all'],
    queryFn: async () => ({ assets: await loadAllAdminAssets(api), nextCursor: null }),
  });
}

function SessionState({ title }: { title: string }) {
  return (
    <main>
      <StartupScreen title={title} description="Preparing your workspace…" />
    </main>
  );
}

function EditorLoading() {
  return (
    <div className="editor-skeleton" aria-label="Loading content">
      <span />
      <span />
      <span />
    </div>
  );
}

function EditorError({ onRetry }: { onRetry(): void }) {
  return (
    <section className="empty-state">
      <p className="eyebrow">Connection</p>
      <h1>Content could not be loaded</h1>
      <p>Your draft has not been changed. Check the API connection and try again.</p>
      <button className="button button--primary" type="button" onClick={onRetry}>
        Try again
      </button>
    </section>
  );
}

function newJournalPost(assetId: string): CreateJournalPostRequest {
  const slug = `new-article-${Date.now()}`;
  return {
    slug,
    siteAccess: 'AdminOnly',
    payload: {
      title: 'Untitled article',
      excerpt: 'Add a short introduction for this article.',
      category: 'Journal',
      dateLabel: new Intl.DateTimeFormat('en-GB', {
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
      publishedAt: new Date().toISOString(),
      cover: { assetId },
      seo: {
        title: 'Untitled article — Diana Perova',
        description: 'Photography notes and guides by Diana Perova.',
        noindex: true,
      },
    },
    sections: [
      {
        id: crypto.randomUUID(),
        type: 'RichText',
        position: 0,
        payload: { body: plainToRichText('Begin the article here.') },
      },
    ],
  };
}
